import React, { useEffect, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { CheckCircle2, Loader2, XCircle, ArrowRight } from "lucide-react";

const MAX_ATTEMPTS = 8;
const INTERVAL_MS = 2000;

export default function BookingSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ phase: "polling", data: null });
  const attemptsRef = useRef(0);

  useEffect(() => {
    let cancelled = false;
    if (!sessionId) {
      setState({ phase: "missing", data: null });
      return;
    }

    const poll = async () => {
      if (cancelled) return;
      try {
        const res = await api.get(`/desk/checkout/status/${sessionId}`);
        const data = res.data;
        if (data.payment_status === "paid") {
          setState({ phase: "paid", data });
          return;
        }
        if (data.status === "expired") {
          setState({ phase: "expired", data });
          return;
        }
        attemptsRef.current += 1;
        if (attemptsRef.current >= MAX_ATTEMPTS) {
          setState({ phase: "timeout", data });
          return;
        }
        setTimeout(poll, INTERVAL_MS);
      } catch (e) {
        attemptsRef.current += 1;
        if (attemptsRef.current >= MAX_ATTEMPTS) {
          setState({ phase: "error", data: null });
          return;
        }
        setTimeout(poll, INTERVAL_MS);
      }
    };
    poll();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  return (
    <main className="max-w-[1100px] mx-auto px-6 md:px-12 py-24 md:py-32 min-h-[70vh]" data-testid="booking-success-page">
      {state.phase === "polling" && (
        <div data-testid="status-polling">
          <p className="overline" style={{ color: "var(--accent)" }}>Even geduld</p>
          <h1 className="font-serif text-4xl md:text-6xl font-light leading-tight mt-5">
            Je betaling bevestigen…
          </h1>
          <p className="mt-6 max-w-xl text-base" style={{ color: "var(--text-secondary)" }}>
            We controleren even bij Stripe. Dit duurt meestal slechts enkele
            seconden.
          </p>
          <div className="mt-10 flex items-center gap-3" style={{ color: "var(--text-secondary)" }}>
            <Loader2 className="animate-spin" size={18} />
            <span className="overline">Status controleren</span>
          </div>
        </div>
      )}

      {state.phase === "paid" && (
        <div data-testid="status-paid">
          <CheckCircle2 size={36} style={{ color: "var(--accent)" }} />
          <p className="overline mt-6" style={{ color: "var(--accent)" }}>Bevestigd</p>
          <h1 className="font-serif text-4xl md:text-6xl font-light leading-tight mt-4">
            Je bureau is gereserveerd.
          </h1>
          <p className="mt-6 max-w-xl text-base md:text-lg leading-relaxed"
             style={{ color: "var(--text-secondary)" }}>
            Dankjewel{state.data?.metadata?.name ? `, ${state.data.metadata.name.split(" ")[0]}` : ""}.
            we hebben je toegangscode, werkplek en wifi-welkomstbericht
            verstuurd naar {state.data?.metadata?.email || "je inbox"}. We
            zetten alvast een espresso klaar.
          </p>
          <div className="card-flat mt-12 p-8 grid grid-cols-2 md:grid-cols-4 gap-6">
            <div>
              <p className="overline" style={{ color: "var(--text-secondary)" }}>Pas</p>
              <p className="font-serif text-xl mt-1">{state.data?.metadata?.package_id || "···"}</p>
            </div>
            <div>
              <p className="overline" style={{ color: "var(--text-secondary)" }}>Datum</p>
              <p className="font-serif text-xl mt-1">{state.data?.metadata?.date || "···"}</p>
            </div>
            <div>
              <p className="overline" style={{ color: "var(--text-secondary)" }}>Betaald</p>
              <p className="font-serif text-xl mt-1">
                {state.data
                  ? `${(state.data.amount_total / 100).toFixed(2)} ${state.data.currency?.toUpperCase()}`
                  : "···"}
              </p>
            </div>
            <div>
              <p className="overline" style={{ color: "var(--text-secondary)" }}>Status</p>
              <p className="font-serif text-xl mt-1" style={{ color: "var(--accent)" }}>Bevestigd</p>
            </div>
          </div>

          <Link to="/" className="btn-ghost mt-12 inline-flex items-center gap-2" data-testid="post-paid-home">
            Terug naar het pand <ArrowRight size={14} />
          </Link>
        </div>
      )}

      {(state.phase === "timeout" || state.phase === "error") && (
        <div data-testid="status-timeout">
          <p className="overline" style={{ color: "var(--accent)" }}>Nog bezig</p>
          <h1 className="font-serif text-4xl md:text-6xl font-light leading-tight mt-5">
            We bevestigen je betaling nog bij Stripe.
          </h1>
          <p className="mt-6 max-w-xl text-base" style={{ color: "var(--text-secondary)" }}>
            Geen zorgen. Als je kaart is belast, krijg je binnenkort een
            bevestiging per e-mail. Je kan deze pagina veilig vernieuwen of
            over enkele minuten terugkomen.
          </p>
          <Link to="/book-desk" className="btn-ghost mt-10 inline-block">Terug naar boekingen</Link>
        </div>
      )}

      {state.phase === "expired" && (
        <div data-testid="status-expired">
          <XCircle size={36} style={{ color: "var(--accent)" }} />
          <h1 className="font-serif text-4xl md:text-6xl font-light leading-tight mt-6">
            Die sessie is verlopen.
          </h1>
          <p className="mt-6" style={{ color: "var(--text-secondary)" }}>
            Er werd niets afgeschreven. Je kan hieronder een nieuwe boeking
            starten.
          </p>
          <Link to="/book-desk" className="btn-primary mt-10 inline-flex">Opnieuw proberen</Link>
        </div>
      )}

      {state.phase === "missing" && (
        <div data-testid="status-missing">
          <h1 className="font-serif text-4xl md:text-6xl font-light leading-tight">
            Hier valt niets te bevestigen.
          </h1>
          <p className="mt-6" style={{ color: "var(--text-secondary)" }}>
            Deze pagina dient om een bureauboeking te bevestigen. Ga terug
            naar de homepage om er een te starten.
          </p>
          <Link to="/" className="btn-ghost mt-10 inline-block">Terug naar home</Link>
        </div>
      )}
    </main>
  );
}
